/**
 * Read-only rich markdown renderer for the Design tab — renders markdown
 * (including GFM tables, task lists, code blocks) via the same TipTap engine
 * the section editor uses, so previews look exactly like the editable surface.
 * Used for wizard / Hammer draft previews where Foundry's lightweight
 * MarkdownContent can't render tables.
 */
import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { buildRichExtensions } from './lib/editor-extensions';
import { MarkdownContent } from '../../components/MarkdownContent';
import { TrustedHtmlFallbackBoundary } from './lib/TrustedHtmlFallbackBoundary';
import { shouldUseTrustedHtmlFallbackForSession } from './lib/trusted-types';

export interface RichMarkdownProps {
  content: string;
  className?: string;
  /** Makes inline `#tag` tokens clickable (tag view). */
  onTagClick?: (tag: string) => void;
  /** Makes inline `@Mention` tokens clickable (navigate to the named concept). */
  onMentionClick?: (name: string) => void;
}

export function RichMarkdown({
  content,
  className,
  onTagClick,
  onMentionClick,
}: RichMarkdownProps): ReactElement {
  const rootClassName = `design-wysiwyg ${className ?? ''}`.trim();
  const fallback = <MarkdownContent content={content} className={rootClassName} />;
  if (shouldUseTrustedHtmlFallbackForSession()) return fallback;

  return (
    <TrustedHtmlFallbackBoundary fallback={fallback}>
      <TiptapRichMarkdown
        content={content}
        className={rootClassName}
        onTagClick={onTagClick}
        onMentionClick={onMentionClick}
      />
    </TrustedHtmlFallbackBoundary>
  );
}

function TiptapRichMarkdown({
  content,
  className,
  onTagClick,
  onMentionClick,
}: {
  content: string;
  className: string;
  onTagClick?: (tag: string) => void;
  onMentionClick?: (name: string) => void;
}): ReactElement {
  // Extensions capture the click handlers at editor creation; route through
  // refs so the latest closures are always the ones that fire.
  const tagClickRef = useRef(onTagClick);
  const mentionClickRef = useRef(onMentionClick);
  useEffect(() => {
    tagClickRef.current = onTagClick;
    mentionClickRef.current = onMentionClick;
  }, [onTagClick, onMentionClick]);

  const editor = useEditor({
    extensions: buildRichExtensions({
      onTagClick: onTagClick ? (tag) => tagClickRef.current?.(tag) : undefined,
      onMentionClick: onMentionClick ? (name) => mentionClickRef.current?.(name) : undefined,
    }),
    content,
    editable: false,
    editorProps: { attributes: { class: className } },
  });

  // Re-render when the source markdown changes (streaming draft, switched item).
  useEffect(() => {
    if (!editor) return;
    const current = editor.storage.markdown.getMarkdown() as string;
    if (content !== current) editor.commands.setContent(content, false);
  }, [content, editor]);

  return <EditorContent editor={editor} />;
}
